/**
 * Money utilities for BizOP.
 *
 * Money is stored everywhere as BigInt cents (or smallest currency unit).
 * NEVER as floating-point numbers — float arithmetic loses precision.
 *
 * Most currencies use 2 decimal places (USD, BDT, EUR, GBP, INR).
 * A few don't:
 *   - JPY, KRW, VND have 0 decimals
 *   - BHD, IQD, JOD, KWD, OMR, TND have 3 decimals
 *
 * `decimalsFor(code)` resolves the right scale per currency.
 *
 * FX rates are stored as `rate * 1e8` (also BigInt) to avoid float drift.
 * Multiply: amountInBase = amountCents * fxRateE8 / 1e8.
 */

/** Currencies with non-2 decimal places. Default elsewhere = 2. */
const CURRENCY_DECIMALS: Record<string, number> = {
  // 0 decimals
  JPY: 0,
  KRW: 0,
  VND: 0,
  ISK: 0,
  CLP: 0,
  // 3 decimals
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  OMR: 3,
  TND: 3,
  LYD: 3,
};

export function decimalsFor(currencyCode: string): number {
  return CURRENCY_DECIMALS[currencyCode.toUpperCase()] ?? 2;
}

/** Smallest unit per major unit. e.g. USD → 100, JPY → 1, BHD → 1000. */
export function minorPerMajor(currencyCode: string): bigint {
  return 10n ** BigInt(decimalsFor(currencyCode));
}

/**
 * Convert a major-unit number (e.g. 12.50) to BigInt cents.
 * Clamps to currency's decimal scale; rounds half-to-even (banker's).
 */
export function toMinorUnits(amountMajor: number | string, currencyCode: string): bigint {
  const decimals = decimalsFor(currencyCode);
  const scale = 10 ** decimals;
  const n = typeof amountMajor === "string" ? Number.parseFloat(amountMajor) : amountMajor;
  if (!Number.isFinite(n)) throw new Error(`Invalid money amount: ${amountMajor}`);
  // Banker's rounding: round half to even.
  const scaled = n * scale;
  const rounded = Math.round(scaled);
  // Adjust for half-to-even when remainder is exactly 0.5
  const diff = Math.abs(scaled - Math.trunc(scaled));
  if (Math.abs(diff - 0.5) < 1e-9) {
    const truncated = Math.trunc(scaled);
    return BigInt(truncated % 2 === 0 ? truncated : truncated + Math.sign(scaled));
  }
  return BigInt(rounded);
}

/** Convert BigInt cents back to a major-unit number. Use for display only. */
export function toMajorUnits(amountMinor: bigint, currencyCode: string): number {
  const decimals = decimalsFor(currencyCode);
  return Number(amountMinor) / 10 ** decimals;
}

/**
 * Convert an amount in source currency to base currency cents using a snapshot rate.
 * `fxRateE8` is the rate * 1e8 (e.g. 1 USD = 110.5 BDT → fxRateE8 = 11050000000n).
 *
 * Math: amountInBase = amountCents * fxRateE8 / 1e8
 */
export function convertToBase(amountMinor: bigint, fxRateE8: bigint): bigint {
  return (amountMinor * fxRateE8) / 100_000_000n;
}

/** Pack a money triple into the canonical schema shape used in the database. */
export type MoneyFields = {
  currency: string;
  amountCents: bigint;
  fxRateToBaseE8: bigint;
  amountInBaseCents: bigint;
};

export function buildMoney(
  amountCents: bigint,
  currency: string,
  fxRateToBaseE8: bigint,
): MoneyFields {
  return {
    currency: currency.toUpperCase(),
    amountCents,
    fxRateToBaseE8,
    amountInBaseCents: convertToBase(amountCents, fxRateToBaseE8),
  };
}

/** Add two money values in the SAME currency. Throws on currency mismatch. */
export function addMoney(a: MoneyFields, b: MoneyFields): MoneyFields {
  if (a.currency !== b.currency) {
    throw new Error(`Cannot add money in different currencies: ${a.currency} vs ${b.currency}`);
  }
  // Average the FX rates weighted by amount (rare path; most adds happen at posting time
  // where both rows already share the same fx snapshot).
  const totalCents = a.amountCents + b.amountCents;
  const fxRate =
    totalCents === 0n
      ? a.fxRateToBaseE8
      : (a.amountCents * a.fxRateToBaseE8 + b.amountCents * b.fxRateToBaseE8) / totalCents;
  return buildMoney(totalCents, a.currency, fxRate);
}

/** Multiply by an integer quantity. Use for line totals (qty * unit price). */
export function multiplyMoney(money: MoneyFields, quantity: number | bigint): MoneyFields {
  const q = typeof quantity === "bigint" ? quantity : BigInt(Math.round(quantity));
  return buildMoney(money.amountCents * q, money.currency, money.fxRateToBaseE8);
}
